const INVALID_FIELD_SELECTORS = [
  '.control.is-invalid',
  '.multiselect.is-invalid',
  'input.is-invalid',
  'textarea.is-invalid',
  'select.is-invalid',
].join(', ');

export function resolveDomElement(refOrElement) {
  if (!refOrElement) {
    return null;
  }
  if (refOrElement instanceof HTMLElement) {
    return refOrElement;
  }
  if (typeof refOrElement === 'string') {
    return document.querySelector(refOrElement);
  }
  if (refOrElement.$el instanceof HTMLElement) {
    return refOrElement.$el;
  }
  return null;
}

function focusInvalidField(invalidField) {
  const focusable = invalidField.querySelector(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'
  );
  if (focusable) {
    focusable.focus({ preventScroll: true });
  }
}

function scrollElementInContainer(scrollTarget, scrollContainer, { behavior, block }) {
  const containerRect = scrollContainer.getBoundingClientRect();
  const targetRect = scrollTarget.getBoundingClientRect();
  let targetScrollTop = scrollContainer.scrollTop + (targetRect.top - containerRect.top);

  if (block === 'center') {
    targetScrollTop -= (scrollContainer.clientHeight / 2) - (targetRect.height / 2);
  }

  scrollContainer.scrollTo({ top: targetScrollTop, behavior });
}

function isScrollableContainer(element) {
  const { overflowY } = window.getComputedStyle(element);
  return ['auto', 'scroll', 'overlay'].includes(overflowY)
    && element.scrollHeight > element.clientHeight;
}

/**
 * Scrolls the view to the first invalid field inside a container.
 * Relies on vee-validate CSS classes configured in the app (`.control.is-invalid`, etc.).
 */
export function scrollToFirstInvalidField(refOrElement, options = {}) {
  const root = resolveDomElement(refOrElement);
  if (!root) {
    return false;
  }

  const {
    behavior = 'smooth',
    block = 'center',
    focus = true,
    scrollContainer = null,
  } = options;

  const invalidField = root.querySelector(INVALID_FIELD_SELECTORS);
  if (!invalidField) {
    return false;
  }

  const scrollTarget = invalidField.closest('.form-group') || invalidField;
  const container = resolveDomElement(scrollContainer);

  if (container && container.contains(scrollTarget) && isScrollableContainer(container)) {
    scrollElementInContainer(scrollTarget, container, { behavior, block });
  } else {
    scrollTarget.scrollIntoView({ behavior, block });
  }

  if (focus) {
    focusInvalidField(invalidField);
  }

  return true;
}

/**
 * Scrolls each provided section to its own first invalid field.
 * Useful for pages with independent scroll areas (main form + sidebar).
 */
export function scrollToFirstInvalidFieldsInSections(sections, options = {}) {
  const {
    behavior = 'smooth',
    block = 'center',
    focus = true,
  } = options;

  let scrolledAny = false;
  let hasFocused = false;

  sections.forEach((section) => {
    const didScroll = scrollToFirstInvalidField(section, {
      behavior,
      block,
      focus: focus && !hasFocused,
      scrollContainer: section,
    });

    if (didScroll) {
      scrolledAny = true;
      hasFocused = true;
    }
  });

  return scrolledAny;
}

/**
 * Runs vee-validate ValidationObserver.validate() and scrolls to invalid fields on failure.
 * Pass `scrollSections` to scroll independently inside multiple containers.
 */
export async function validateFormAndScroll(validationObserver, vueInstance, options = {}) {
  if (!validationObserver || typeof validationObserver.validate !== 'function') {
    return true;
  }

  const isValid = await validationObserver.validate();
  if (!isValid && vueInstance && typeof vueInstance.$nextTick === 'function') {
    await vueInstance.$nextTick();

    const { scrollSections, ...scrollOptions } = options;
    if (scrollSections && scrollSections.length) {
      scrollToFirstInvalidFieldsInSections(scrollSections, scrollOptions);
    } else {
      scrollToFirstInvalidField(validationObserver, scrollOptions);
    }
  }

  return isValid;
}
